import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Layout, { DateInput, SearchableSelect } from "./Layout";
import { formatDate, getApiUrl, getTodayIso } from "./config";

function CustomerLedgerReport() {
  const today = getTodayIso();
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [result, setResult] = useState(null);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await axios.get(`${getApiUrl()}/customers`);
      setCustomers(res.data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchLedger = useCallback(async () => {
    if (!customerId) {
      setResult(null);
      return;
    }
    try {
      const params = { customer_id: customerId };
      if (from) params.from = from;
      if (to) params.to = to;
      const res = await axios.get(`${getApiUrl()}/reports/ledger/customer`, { params });
      setResult(res.data);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.sqlMessage ||
        err?.response?.data?.code ||
        "Failed to load customer ledger";
      alert(message);
    }
  }, [customerId, from, to]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);

  const rows = Array.isArray(result?.rows) ? result.rows : [];
  const opening = Number(result?.opening_balance || 0);
  const closing = Number(result?.closing_balance || 0);

  const totals = useMemo(() => {
    return {
      debit: Number(result?.totals?.debit || 0),
      credit: Number(result?.totals?.credit || 0)
    };
  }, [result]);

  const formatSplit = (row) => {
    const cash = Number(row?.cash_amount) || 0;
    const upi = Number(row?.upi_amount) || 0;
    const bank = Number(row?.bank_amount) || 0;
    const parts = [];
    if (cash > 0) parts.push(`Cash ₹ ${cash.toFixed(2)}`);
    if (upi > 0) parts.push(`UPI ₹ ${upi.toFixed(2)}`);
    if (bank > 0) parts.push(`Bank ₹ ${bank.toFixed(2)}`);
    return parts.length > 0 ? parts.join(" | ") : "-";
  };

  const handleExportPdf = () => {
    window.print();
  };

  return (
    <Layout>
      <div className="container mt-4">
        <h2>Customer Ledger</h2>

        <div className="card p-4 mb-4 no-print">
          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label fw-bold">Customer</label>
              <SearchableSelect
                value={customerId}
                options={customers.map((c) => ({ value: String(c.id), label: c.name || "" }))}
                placeholder="Type customer name..."
                onChange={(nextId) => setCustomerId(nextId)}
                allowClear
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold">From</label>
              <DateInput value={from} onChange={setFrom} />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold">To</label>
              <DateInput value={to} onChange={setTo} />
            </div>
            <div className="col-md-2 d-grid">
              <button type="button" className="btn btn-primary" onClick={fetchLedger} disabled={!customerId}>
                Refresh
              </button>
            </div>
            <div className="col-md-2 d-grid">
              <button type="button" className="btn btn-outline-dark" onClick={handleExportPdf} disabled={!customerId}>
                Export PDF
              </button>
            </div>
          </div>
        </div>

        {customerId && (
          <div className="mb-3">
            <div className="d-flex flex-wrap gap-3">
              <div className="fw-bold">Opening: ₹ {opening.toFixed(2)}</div>
              <div className="fw-bold">Debit: ₹ {totals.debit.toFixed(2)}</div>
              <div className="fw-bold">Credit: ₹ {totals.credit.toFixed(2)}</div>
              <div className="fw-bold">Closing: ₹ {closing.toFixed(2)}</div>
            </div>
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Particulars</th>
                <th>Mode</th>
                <th>Split</th>
                <th className="text-end">Debit</th>
                <th className="text-end">Credit</th>
                <th className="text-end">Balance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={`${r.type}-${r.ref_id}-${idx}`}>
                  <td>{formatDate(r.date)}</td>
                  <td>{r.type}</td>
                  <td>{r.particulars}</td>
                  <td>{r.mode || "-"}</td>
                  <td>{formatSplit(r)}</td>
                  <td className="text-end">₹ {(Number(r.debit) || 0).toFixed(2)}</td>
                  <td className="text-end">₹ {(Number(r.credit) || 0).toFixed(2)}</td>
                  <td className="text-end fw-bold">₹ {(Number(r.balance) || 0).toFixed(2)}</td>
                </tr>
              ))}
              {customerId && rows.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center">
                    No entries
                  </td>
                </tr>
              )}
              {!customerId && (
                <tr>
                  <td colSpan="8" className="text-center">
                    Select a customer
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

export default CustomerLedgerReport;
